import { all, call, put, retry, select, takeLatest } from 'typed-redux-saga'
import { errorHandler, getErrorsNotifications, showError } from 'Modules'
import {
	ApiChatType,
	chatApi,
	ChatModel,
	ChatNS,
	chatSelectors,
	chatSlice,
	delayChat,
	determineSectionBySlug,
	MessageModel,
	MessageType,
	serializeParams,
	userName,
	userRoles,
} from 'Pages/chat'
import { authSelector } from 'Pages/auth'
import { RequestParams } from 'Models'
import { downloadFileFromApi, unionByKey } from 'Helpers'

function* getSessionToken() {
	try {
		yield* put(chatSlice.actions.setIsLoading(true))
		const res = yield* retry(Infinity, 10000, chatApi.getSessionToken)
		yield* put(chatSlice.actions.setSessionToken(res.token))
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(chatSlice.actions.setIsLoading(false))
	}
}

function* getChats(action: ChatNS.GetChats) {
	try {
		yield* put(chatSlice.actions.setIsLoading(true))

		const requestParams = {
			'q[chat_type_eq]': action.payload?.order_id
				? ApiChatType.ORDER
				: determineSectionBySlug(action.payload.section_slug),

			...(action.payload?.order_id
				? {
						'q[meta_info_jcont]': serializeParams({
							neo_order_id: `${action.payload.order_id}`,
						}),
				  }
				: {}),
		}

		const res = yield* call(chatApi.getChats, action.payload.token, requestParams)

		if (res.entries.length) {
			yield* put(chatSlice.actions.setChats(res.entries?.[0]))
		} else {
			yield* call(createChat, action)
		}
	} catch (e) {
		yield* call(showError, getErrorsNotifications()[0])
	} finally {
		yield* put(chatSlice.actions.setIsLoading(false))
	}
}

function* createChat(action: ChatNS.GetChats) {
	const user = yield* select(authSelector.getAuthDataSelector)

	const data = {
		section_slug: action.payload.section_slug,
		section_name: action.payload.section_name,
		territorial_bank_name: user.partner?.manager?.territorial_bank?.name,
		partner_vat_number: user.partner?.vat_number,
		partner_name: user.partner?.name,
		user_email: user.email,
		order_id: action.payload?.order_id,
	}

	const res: ChatModel = yield* call(chatApi.createChat, action.payload.token, data)
	yield* put(chatSlice.actions.setChats(res))
}

function* getMessages(action: ChatNS.GetMessages) {
	try {
		const { token, params } = action.payload
		const isClear = action.payload.clear
		yield* put(chatSlice.actions.setIsLoading(true))
		const { chat_id, page, per_page } = params
		const requestParams = {
			'q[chat_id_eq]': chat_id,
			'q[s]': 'created_at DESC',
		}
		const res = yield* call(
			chatApi.getMessages,
			token,
			new RequestParams(page, per_page, null, requestParams)
		)
		res.entries.reverse()
		const oldMessage = yield* select(chatSelectors.messages)
		if (!isClear) res.entries = res.entries.concat(oldMessage)
		yield* put(chatSlice.actions.setParams(params))
		yield* put(chatSlice.actions.setMessages(res.entries))
		yield* put(chatSlice.actions.setTotalMessages(res.total_entries))
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(chatSlice.actions.setIsLoading(false))
	}
}

function* setIsReadedMessages(action: ChatNS.setIsReadedMessage) {
	try {
		yield* call(chatApi.setUnreadedMessages, action.payload)
		yield* put(chatSlice.actions.setUnreaded(false))
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* setRatingMessage(action: ChatNS.setRatingMessage) {
	try {
		yield* call(chatApi.setRating, action.payload)
		yield* call(delayChat, 2000)
		yield* put(chatSlice.actions.setViewRating(false))
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* uploadFileAction(action: ChatNS.uploadFileAction) {
	try {
		const { chat_id, token, files } = action.payload
		const user = yield* select(authSelector.getAuthDataSelector)
		const currentQuote = yield* select(chatSelectors.currentQuote)

		for (let i = 0; i < files.length; i += 1) {
			const res = yield* call(chatApi.uploadFile, {
				file: files[i],
				chat_id,
				token,
				role_name: userRoles(user),
				user_name: userName(user),
				reply_to_message_id: currentQuote?.quoteMessageId,
			})
			const prevFiles = yield* select(chatSelectors.filesInProgress)
			yield* put(chatSlice.actions.setFilesInProgress([...prevFiles, res]))
		}
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* downloadFile(action: ChatNS.downloadFile) {
	try {
		const { token, file_id, file_name } = action.payload
		const res = yield* call(chatApi.downloadFile, token, file_id)
		const awaitedFileNames = yield* select(chatSelectors.selectAwaitedFiles)
		if (res.status === 202) {
			yield* put(chatSlice.actions.setAwaitedFiles({ ...awaitedFileNames, [file_id]: file_name }))
			return
		}
		const blob = yield* call(async () => res.blob())
		yield* call(downloadFileFromApi, blob, file_name || awaitedFileNames[file_id])
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* selectNextFoundMessage(action: ChatNS.SelectNextFoundMessage) {
	try {
		const { token, message } = action.payload
		yield* put(chatSlice.actions.setIsLoading(true))

		const messages = yield* select(chatSelectors.messages)

		const isLoadedMessage = messages.some((item) => item.id === message.id)

		if (!isLoadedMessage) {
			const res = yield* all([
				call(
					chatApi.getMessages,
					token,
					new RequestParams(null, 15, null, {
						'q[s]': 'created_at DESC',
						'q[id_lteq]': message.id,
						'q[id_gt]': null,
					})
				),
				call(
					chatApi.getMessages,
					token,
					new RequestParams(null, 15, null, {
						'q[s]': 'created_at ASC',
						'q[id_gt]': message.id,
						'q[id_lteq]': null,
					})
				),
			])

			const loadedMessages = [...res[0].entries.reverse(), ...res[1].entries].filter(
				(item) => item.message_type === MessageType.MESSAGE
			)
			yield* put(chatSlice.actions.setMessages(loadedMessages))
		}

		yield* put(chatSlice.actions.setCurrentFoundMessage(message))
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(chatSlice.actions.setIsLoading(false))
	}
}

function* searchMessages(action: ChatNS.SearchMessages) {
	try {
		const { token, chat_id, query } = action.payload
		yield* put(chatSlice.actions.setIsLoading(true))
		const requestParams = {
			chat_id,
			'q[s]': 'created_at DESC',
		}

		const res = yield* call(
			chatApi.fullTextSearch,
			token,
			new RequestParams(null, null, query, requestParams)
		)
		yield* put(chatSlice.actions.setFoundMessages(res.entries))
		if (!res.entries.length) return
		yield* call(
			selectNextFoundMessage,
			chatSlice.actions.selectNextFoundMessage({ token, message: res.entries[0] })
		)
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(chatSlice.actions.setIsLoading(false))
	}
}

function* getMessagesByScroll(action: ChatNS.GetMessagesByScroll) {
	try {
		const { token, params } = action.payload
		yield* put(chatSlice.actions.setIsLoading(true))
		const res = yield* call(chatApi.getMessages, token, params)
		const isTop = params['q[id_lt]']
		const filteredMessages = res.entries.filter((item) => item.message_type === MessageType.MESSAGE)

		if (!filteredMessages.length) return

		const currentMessages = yield* select(chatSelectors.messages)

		const messages = isTop
			? [...filteredMessages.reverse(), ...currentMessages]
			: unionByKey<MessageModel>(currentMessages, filteredMessages, 'id')
		yield* put(chatSlice.actions.setParams(params))
		yield* put(chatSlice.actions.setMessages(messages))
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(chatSlice.actions.setIsLoading(false))
	}
}

function* getChatSettings() {
	try {
		const settings = yield* call(chatApi.getSettings)
		yield* put(chatSlice.actions.setChatSettings(settings))
		// eslint-disable-next-line no-empty
	} catch (error) {}
}

function* searchMessageById(action: ChatNS.SearchMessageById) {
	try {
		const { token, chat_id, message_id } = action.payload
		yield* put(chatSlice.actions.setIsLoading(true))
		const requestParams = {
			'q[chat_id_eq]': chat_id,
			'q[id_eq]': message_id,
		}
		const res = yield* call(
			chatApi.getMessages,
			token,
			new RequestParams(null, 1, null, requestParams)
		)
		if (!res.entries.length) return
		yield* put(chatSlice.actions.setFoundQuoteMessage(res.entries[0]))
		yield* call(
			selectNextFoundMessage,
			chatSlice.actions.selectNextFoundMessage({ token, message: res.entries[0] })
		)
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(chatSlice.actions.setIsLoading(false))
	}
}

export function* chatSaga() {
	yield* takeLatest(chatSlice.actions.getChats.type, getChats)
	yield* takeLatest(chatSlice.actions.getMessages.type, getMessages)
	yield* takeLatest(chatSlice.actions.setIsReadedMessage.type, setIsReadedMessages)
	yield* takeLatest(chatSlice.actions.setRatingMessage.type, setRatingMessage)
	yield* takeLatest(chatSlice.actions.uploadFileAction.type, uploadFileAction)
	yield* takeLatest(chatSlice.actions.downloadFile.type, downloadFile)
	yield* takeLatest(chatSlice.actions.searchMessages.type, searchMessages)
	yield* takeLatest(chatSlice.actions.getMessagesByScroll.type, getMessagesByScroll)
	yield* takeLatest(chatSlice.actions.selectNextFoundMessage.type, selectNextFoundMessage)
	yield* takeLatest(chatSlice.actions.getChatSettings.type, getChatSettings)
	yield* takeLatest(chatSlice.actions.getSessionToken.type, getSessionToken)
	yield* takeLatest(chatSlice.actions.searchMessageById.type, searchMessageById)
}
